import { Box, Chip, FormHelperText, makeStyles, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { DeleteOutline } from '@material-ui/icons';
import { useMaterials } from 'hooks';
import { Material } from 'types';
import { colors } from 'theme';
import SelectWithErrorField from './CustomInputs/SelectWithErrorField';
import { DEFAULT_ITEMS_PER_SELECT } from '../tableFilterContext';

const useStyles = makeStyles({
  material: {
    marginLeft: '24px',
    display: 'flex',
    alignItems: 'center',
  },
  materialsList: {
    marginLeft: 19,
    marginTop: 10,
  },
  selectInput: {
    padding: '7px 0px 0px 9px',
    marginRight: 10,
    height: 31,
    display: 'flex',
    flexAlign: 'center',
  },
  materialLabel: {
    fontSize: '18px',
    marginRight: 33,
    flexGrow: 1,
  },
  materialChip: {
    marginBottom: 10,
  },
  errorMessage: {
    textAlign: 'center',
  },
});

const emitValue = (materials: Material[], onChange: (event: { target: { value: string[] } }) => void) => {
  onChange({
    target: {
      value: materials.map(material => material['@id']),
    },
  });
};

type MaterialPickerProps = {
  onValueChange: (event: { target: { value: string[] } }) => void;
  includeReject?: boolean;
  rejectAlwaysPresent?: boolean;
  materialsAllowed?: string[];
  label: string;
  placeholder: string;
  errorMessage?: string;
  error?: boolean;
  required?: boolean;
  requiredLabel?: string;
  disabled?: boolean;
  value: string[];
};

export const MaterialPicker: React.FC<MaterialPickerProps> = ({
  onValueChange,
  includeReject,
  rejectAlwaysPresent,
  label,
  placeholder,
  errorMessage,
  error,
  required,
  requiredLabel,
  disabled,
  materialsAllowed,
  value,
}) => {
  const classes = useStyles();

  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const [availableMaterials, setAvailableMaterials] = useState<Material[]>([]);
  const [chosenMaterials, setChosenMaterials] = useState<Material[]>([]);
  const [reject, setReject] = useState<Material | null>(null);

  useEffect(() => {
    if (!materials) {
      setAvailableMaterials([]);
      setReject(null);
      return;
    }
    const materialsList =
      undefined === materialsAllowed
        ? materials
        : materials.filter(material => materialsAllowed?.includes(material['@id']));

    const updatedChosenMaterials: Material[] = value
      .map(materialId => materials.find(m => m['@id'] === materialId) ?? null)
      .filter((m): m is Material => m !== null);

    setReject(materialsList.find(material => material.isReject) ?? null);

    if (includeReject && !rejectAlwaysPresent) {
      setAvailableMaterials(materialsList.filter(material => !updatedChosenMaterials.includes(material)));
    } else {
      setAvailableMaterials(
        materialsList.filter(material => !updatedChosenMaterials.includes(material) && !material.isReject),
      );
    }

    setChosenMaterials(updatedChosenMaterials);
  }, [value, materials, materialsAllowed, setAvailableMaterials, setReject, includeReject, rejectAlwaysPresent]);

  const addMaterial = useCallback(
    (materialId: string) => {
      if (!materials) return;
      const selectedMaterial = materials.find(material => material['@id'] === materialId);
      if (!selectedMaterial) return;
      const newChosenMaterials = [...chosenMaterials, selectedMaterial].sort((a, b) => {
        if (a.isReject) return 1;
        if (b.isReject) return -1;
        return a.shortName.localeCompare(b.shortName);
      });
      return newChosenMaterials;
    },
    [materials, chosenMaterials],
  );

  const removeMaterial = useCallback(
    (materialToRemove: Material) => chosenMaterials.filter(material => material !== materialToRemove),
    [chosenMaterials],
  );

  return (
    <div>
      <Typography variant="inherit" className={classes.material}>
        <div className={classes.materialLabel}>
          {label} {required && (requiredLabel ?? '*')}
        </div>
        <SelectWithErrorField
          id="material"
          choices={availableMaterials}
          choiceDisplayKey="shortName"
          placeholder={placeholder}
          InputLabelProps={{ placeholder }}
          error={false}
          errorMessage=""
          classes={{ root: classes.selectInput }}
          disabled={disabled || availableMaterials.length === 0}
          value=""
          onChange={e => {
            const materialsToEmit = addMaterial(e.target.value as string);
            if (!materialsToEmit) {
              emitValue(rejectAlwaysPresent && reject !== null ? [reject] : [], onValueChange);
              return;
            }
            emitValue(
              rejectAlwaysPresent && reject !== null ? [...materialsToEmit, reject] : materialsToEmit,
              onValueChange,
            );
          }}
        />
      </Typography>
      <Box className={classes.materialsList}>
        {chosenMaterials.map(material => (
          <Chip
            key={material['@id']}
            className={classes.materialChip}
            label={material.shortName}
            onDelete={() => {
              const materialsToEmit = removeMaterial(material);
              emitValue(
                rejectAlwaysPresent && reject !== null ? [...materialsToEmit, reject] : materialsToEmit,
                onValueChange,
              );
            }}
            deleteIcon={<DeleteOutline style={{ color: colors.white }} />}
          />
        ))}
        {rejectAlwaysPresent && reject !== null && <Chip label={reject.shortName} />}
      </Box>
      {error && (
        <FormHelperText className={classes.errorMessage} error>
          {errorMessage}
        </FormHelperText>
      )}
    </div>
  );
};
